import * as daccaApi from '../../utils/api/daccaApi';
import * as documentApi from '../../utils/api/documentsApi';
import * as mapper from './mapper';
import * as policyRimBusiness from '../../../business/logic/policy-business-logic-rim';
import * as selectors from './selectors';

import { CalculationUsageTC, ReportTypeTC } from '../../../business/models/typecode.enums';
import { DaccaActionTypes, DaccaCalculation, OverallTotal } from './types';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import {
  fetchDaccaCalculations,
  fetchDaccaCalculationsError,
  fetchDaccaCalculationsSuccess,
  importLinesFromDacca,
  importLinesFromDaccaError,
  importLinesFromDaccaSuccess
} from './actions';

import { UpdateDaccaCalculationRequest } from './model';
import { getI18n } from '../../utils/i18n/strings.manager';
import { importLinesFromDaccaSuccess as importRimLinesFromDaccaSuccess } from '../rim/actions';
import { importLinesFromDaccaSuccess as importSvsLinesFromDaccaSuccess } from '../svs/actions';
import { Effect } from 'redux-saga/effects'

const localization = getI18n();

function* handleFetchDaccaCalculations(action: ReturnType<typeof fetchDaccaCalculations>): Generator<Effect, void, any> {
  try {
    const res = yield call(daccaApi.getDaccaCalculations, action.payload.caseCode);

    if (!res.data) {
      yield put(fetchDaccaCalculationsError(JSON.stringify(res.data)));
    } else {
      yield put(fetchDaccaCalculationsSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchDaccaCalculationsError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchDaccaCalculationsError('An unknown error occurred.'));
    }
  }
}

function* handleImportLinesFromDacca(action: ReturnType<typeof importLinesFromDacca>): Generator<Effect, void, any> {
  try {
    yield call(handleFetchDaccaCalculations, {
      type: DaccaActionTypes.FETCH_DACCA_CALCULATIONS,
      payload: { caseCode: action.payload.caseCode },
    });

    const daccaCalculations = (yield select(selectors.daccaCalculations)) as DaccaCalculation[];

    switch (action.payload.reportType) {
      case ReportTypeTC.Rim:
        const diyOverallTotal: OverallTotal[] = [];
        const finOverallTotal: OverallTotal[] = [];
        const diyCalculationId = daccaCalculations.filter(p => p.calculationUsageTC === CalculationUsageTC.Diy)[0]?.calculationVersionID;
        const finCalculationId = daccaCalculations.filter(p => p.calculationUsageTC === CalculationUsageTC.Fin)[0]?.calculationVersionID;

        if (diyCalculationId) {
          const diyCalculation = yield call(daccaApi.getDaccaCalculationsDetails, diyCalculationId);
          const updateDocumentRequest: UpdateDaccaCalculationRequest = {
            id: 0,
            reference: action.payload.reference,
            choiceTC: diyCalculation.data.choiceTC,
            createDate: diyCalculation.data.createDate,
            versionNumber: diyCalculation.data.versionNumber,
            reportFileID: diyCalculation.data.reportFileID,
          };
          yield call(documentApi.updateDaccaCalculation, action.payload.caseCode, updateDocumentRequest);
          diyOverallTotal.push(...(diyCalculation.data.result.overallTotals as OverallTotal[]));
        }

        if (finCalculationId) {
          const finCalculation = yield call(daccaApi.getDaccaCalculationsDetails, finCalculationId);
          const updateDocumentRequest: UpdateDaccaCalculationRequest = {
            id: 0,
            reference: action.payload.reference,
            choiceTC: finCalculation.data.choiceTC,
            createDate: finCalculation.data.createDate,
            versionNumber: finCalculation.data.versionNumber,
            reportFileID: finCalculation.data.reportFileID,
          };
          yield call(documentApi.updateDaccaCalculation, action.payload.caseCode, updateDocumentRequest);
          finOverallTotal.push(...(finCalculation.data.result.overallTotals as OverallTotal[]));
        }

        const diyLineDescription = localization.get('details.rim.daccaDiyLineDescription');
        const finLineDescription = localization.get('details.rim.daccaFinLineDescription');
        const policyConditions = (yield select(selectors.policyConditions)) as string;
        const isCocoon = policyRimBusiness.isCocoon(policyConditions);

        yield put(importRimLinesFromDaccaSuccess(mapper.mapDaccaOverallTotalToRimLines(diyOverallTotal, finOverallTotal, diyLineDescription, finLineDescription, isCocoon)));
        break;

      case ReportTypeTC.Svs:
        const rikCalculationId = daccaCalculations.filter(p => p.calculationUsageTC === CalculationUsageTC.Rik)[0]?.calculationVersionID;

        if (rikCalculationId) {
          const rikCalculation = yield call(daccaApi.getDaccaCalculationsDetails, rikCalculationId);
          const updateDocumentRequest: UpdateDaccaCalculationRequest = {
            id: 0,
            reference: action.payload.reference,
            choiceTC: rikCalculation.data.choiceTC,
            createDate: rikCalculation.data.createDate,
            versionNumber: rikCalculation.data.versionNumber,
            reportFileID: rikCalculation.data.reportFileID,
          };
          yield call(documentApi.updateDaccaCalculation, action.payload.caseCode, updateDocumentRequest);
        }

        yield put(importSvsLinesFromDaccaSuccess());
        break;

      default:
        break;
    }

    yield put(importLinesFromDaccaSuccess());
  } catch (err) {
    if (err instanceof Error) {
      yield put(importLinesFromDaccaError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(importLinesFromDaccaError('An unknown error occurred.'));
    }
  }
}

function* watchDacca() {
  yield takeEvery(DaccaActionTypes.FETCH_DACCA_CALCULATIONS, handleFetchDaccaCalculations);
  yield takeEvery(DaccaActionTypes.IMPORT_LINES_FROM_DACCA, handleImportLinesFromDacca);
}

function* daccaSaga() {
  yield all([fork(watchDacca)]);
}

export default daccaSaga;
